// Bilingual copy, Indonesian first. Every user facing string in the app
// lives here, components never hardcode copy. `id` is the default locale.
//
// The two dictionaries are kept structurally identical by the type below:
// `id` is declared as Record<TranslationKey, string>, so adding a key to
// `en` without adding it to `id` is a compile error, and i18n.test.ts
// asserts the same thing at runtime.

export type Locale = "id" | "en";

export const LOCALES: Locale[] = ["id", "en"];

const en = {
  "app.title": "traveldiary",

  "nav.map": "Map",
  "nav.capture": "Capture",
  "nav.diary": "Diary",
  "nav.trips": "Trips",
  "nav.explore": "Explore",

  "auth.title": "Sign in",
  "auth.signIn": "Sign in",
  "auth.signUp": "Create account",
  "auth.lede": "This diary is public. Travellers see each other on the live map.",
  "auth.username": "Username",
  "auth.password": "Password",
  "auth.displayName": "Name other travellers see",
  "auth.toRegister": "New here, create an account",
  "auth.toLogin": "I already have an account",
  "auth.working": "Please wait",
  "auth.rejected": "That username and password do not match.",
  "auth.unreachable": "Cannot reach the server. Try again in a moment.",
  "auth.demoHint": "Demo account: brian with the password brian.",
  "auth.signOut": "Sign out",

  "sync.synced": "Synced",
  "sync.syncing": "Syncing",
  "sync.pending": "Pending",
  "sync.offline": "Offline",

  "map.recenter": "Recenter",
  "map.follow": "Follow me",
  "map.unfollow": "Stop following",
  "map.dimension2d": "Flat view",
  "map.dimension3d": "Tilted view",
  "map.street": "Street basemap",
  "map.satellite": "Satellite basemap",
  "map.locating": "Finding your position",
  "map.locationDenied": "Location access is off. Turn it on to record your journey.",
  "map.loadFailed": "The map could not load.",
  "map.noTrip": "No active trip",
  "map.noTripHint": "Open Trips and set one trip as active to start recording.",
  "map.accuracy": "Accuracy",
  "map.you": "You are here",
  "map.points": "Track points",
  "map.travellersHere": "Travellers here",
  "map.momentsHere": "Moments here",
  "map.momentPin": "Diary moment",
  "map.youSuffix": "(you)",
  "map.liveTravellers": "Live travellers",
  "map.placeTitle": "This destination",
  "map.destinations": "Destinations here:",
  "map.diariesHere": "Diaries here",
  "map.travellerCount": "Travellers with a diary here:",
  "map.backToPlace": "Back to this destination",
  "map.openTimeline": "Full timeline",
  "map.showLive": "Show travellers online now",
  "map.hideLive": "Hide travellers online now",

  "feed.like": "Like this entry",
  "feed.unlike": "Remove your like",
  "feed.comments": "Comments",
  "feed.commentPlaceholder": "Write a comment",
  "feed.send": "Send comment",
  "feed.commentsUnavailable": "Comments are not available right now.",

  "explore.title": "Explore",
  "explore.empty": "No public moments yet",
  "explore.emptyHint": "When other travellers post, their moments land here.",
  "explore.failed": "The public feed could not load.",
  "explore.someone": "A traveller",
  "explore.noPlace": "Unnamed place",
  "explore.entries": "entries",
  "explore.showOnMap": "Show on the map",
  "explore.timelineEmpty": "No public moments",
  "explore.timelineEmptyHint": "This traveller has not shared anything yet.",
  "explore.timelineFailed": "That timeline could not load.",

  "capture.title": "Capture",
  "capture.viewfinder": "Viewfinder",
  "capture.shutter": "Take a photo",
  "capture.shutterHint": "Tap for a photo, press and hold for video.",
  "capture.recording": "Recording",
  "capture.videoLimit": "Video stops automatically at 60 seconds.",
  "capture.gallery": "Upload from gallery",
  "capture.cameraUnavailable": "The camera is unavailable here. Upload from the gallery instead.",
  "capture.selected": "Selected media",
  "capture.remove": "Remove",
  "capture.next": "Next",
  "capture.back": "Back to the photos",
  "capture.caption": "Caption",
  "capture.captionPlaceholder": "Write what happened here.",
  "capture.location": "Location",
  "capture.locationFromGps": "From GPS at capture time",
  "capture.locationFromExif": "From the photo metadata",
  "capture.locationManual": "Set by you",
  "capture.locationMissing": "No location found. Enter it below so the entry is not lost.",
  "capture.latitude": "Latitude",
  "capture.longitude": "Longitude",
  "capture.placeName": "Place name",
  "capture.placeNamePlaceholder": "Optional, for example Kawah Ijen",
  "capture.time": "Time",
  "capture.privacy": "Privacy",
  "capture.save": "Save entry",
  "capture.saving": "Saving",
  "capture.savedOnline": "Entry saved.",
  "capture.savedQueued": "Entry queued, it will sync when you are back online.",
  "capture.needMedia": "Add at least one photo or video first.",
  "capture.mediaFull": "Five photos is the limit for one diary entry.",
  "capture.writeLater": "Write it later, upload from the gallery",
  "capture.locationHow": "How to set the location",
  "capture.locationMap": "Drop a pin",
  "capture.locationSearch": "Address",
  "capture.locationPlaces": "Places",
  "capture.locationSearchPlaceholder": "Street, place or city",
  "capture.locationSearchGo": "Search this address",
  "capture.locationSearchHint": "Searched when you submit, not while you type.",
  "capture.locationSearchEmpty": "No address matched that search.",
  "capture.locationSearchFailed": "Address search is not available right now. Drop a pin instead.",
  "capture.locationPlacesPlaceholder": "Filter destinations",
  "capture.locationPlacesEmpty": "No destination matches that filter yet.",
  "capture.locationNone": "No location set. The entry still saves, it just gets no map pin.",
  "capture.locationClear": "Clear the location",
  "capture.locationFromPhoto": "From the photo",
  "capture.locationPicked": "Chosen by you",
  "capture.timeFromExif": "From the photo metadata",
  "capture.timeManual": "Set by you",
  "capture.timeNow": "Right now",
  "capture.timeFuture": "A diary entry cannot be in the future.",
  "capture.videoUnsupported": "This browser records a video format the server rejects. The entry stays in the queue, add a photo or record on a phone.",

  "diary.title": "Diary",
  "diary.viewFeed": "Feed",
  "diary.viewCalendar": "Calendar",
  "diary.empty": "No entries yet",
  "diary.emptyHint": "Capture a photo and write a caption to start your diary.",
  "diary.entryCount": "entries",
  "diary.entryCountOne": "entry",
  "diary.noCaption": "No caption",
  "diary.queued": "Waiting to sync",
  "diary.prevMonth": "Previous month",
  "diary.nextMonth": "Next month",

  "trips.title": "Trips",
  "trips.create": "New trip",
  "trips.formTitle": "Create a trip",
  "trips.fieldTitle": "Title",
  "trips.fieldTitlePlaceholder": "For example Java overland",
  "trips.fieldStart": "Start date",
  "trips.fieldEnd": "End date",
  "trips.fieldDescription": "Description",
  "trips.fieldTransport": "Transport",
  "trips.save": "Save trip",
  "trips.cancel": "Cancel",
  "trips.empty": "No trips yet",
  "trips.emptyHint": "Create your first trip to start recording a journey.",
  "trips.active": "Active",
  "trips.setActive": "Set active",
  "trips.history": "Show on the map",
  "trips.historyHint": "Every entry from this trip, pinned and in the order they happened.",
  "trips.statMoments": "Moments",
  "trips.statDays": "Days",
  "trips.statPoints": "Points",
  "trips.statDistance": "Distance",
  "trips.loadFailed": "Trips could not be loaded. Local trips are shown.",
  "trips.refresh": "Refresh",
  "trips.until": "to",

  "settings.title": "Settings",
  "settings.language": "Language",
  "settings.indonesian": "Indonesian",
  "settings.english": "English",
  "settings.displayName": "Your name",
  "settings.displayNamePlaceholder": "Shown to trip members",
  "settings.pendingItems": "Items waiting to sync",
  "settings.syncNow": "Sync now",

  "privacy.private": "Private",
  "privacy.friends": "Friends",
  "privacy.public": "Public",
  "privacy.hint.private": "Only you can see this.",
  "privacy.hint.friends": "Trip members can see this.",
  "privacy.hint.public": "Anyone with the link can see this.",

  "transport.walk": "Walk",
  "transport.bus": "Bus",
  "transport.train": "Train",
  "transport.car": "Car",
  "transport.motorbike": "Motorbike",
  "transport.bicycle": "Bicycle",
  "transport.plane": "Plane",

  "nav.home": "Home",
  "nav.timeline": "Timeline",
  "nav.discover": "Discover",
  "nav.profile": "Profile",

  "home.title": "Home",
  "home.features": "Everything else",
  "home.activity": "Recent activity",
  "home.activityAll": "Open the timeline",
  "home.activityEmpty": "Nothing has been posted yet",
  "home.activityEmptyHint": "When a traveller publishes an entry it shows up here.",
  "home.activeTrip": "Active trip",
  "home.noTrip": "No active trip",
  "home.statPlaces": "Destinations",
  "home.statSaved": "Wishlist",

  "cari.title": "Discover",
  "discover.lede": "One destination at a time. Save it or skip it.",
  "discover.hint": "Drag the card, or use the two controls below.",
  "discover.accept": "Save to wishlist",
  "discover.acceptShort": "Save",
  "discover.dismiss": "Not interested",
  "discover.dismissShort": "Skip",
  "discover.saved": "Saved to your wishlist",
  "discover.skipped": "Dismissed",
  "discover.empty": "You have seen every destination",
  "discover.emptyHint": "Undo the last one, or open your wishlist to plan a trip.",
  "discover.none": "No destination to show yet",
  "discover.noneHint": "Destinations appear once travellers post public photos.",
  "discover.failed": "The deck could not load.",
  "discover.budget": "Budget estimate",
  "discover.budgetDummy": "Placeholder estimate for this prototype, not a real price.",
  "discover.perPerson": "per person",
  "discover.travellers": "travellers wrote here",
  "discover.openWishlist": "Open the wishlist",
  "discover.of": "of",

  "search.title": "Search",
  "search.open": "Open search",
  "search.placeholder": "Places, travellers, entries",
  "search.clear": "Clear the search",
  "search.grid": "Explore",
  "search.gridEmpty": "No public photos yet",
  "search.gridEmptyHint": "Public entries with a photo land in this grid.",
  "search.noResults": "Nothing matched that search",
  "search.noResultsHint": "Try a place name, a traveller, or a word from a caption.",
  "search.places": "Places",
  "search.travellers": "Travellers",
  "search.moments": "Entries",
  "search.typeAll": "All",
  "search.typePlace": "Places",
  "search.typeTraveller": "Travellers",
  "search.typeMoment": "Entries",

  "profil.title": "Profile",
  "profile.account": "Your account",
  "profile.bio": "Bio",
  "profile.bioPlaceholder": "One or two lines about how you travel",
  "profile.save": "Save profile",
  "profile.saved": "Profile saved.",
  "profile.failed": "The profile could not be saved.",
  "profile.avatar": "Your character",
  "profile.avatarNote": "Renaming yourself never changes your character. The seed is your identity.",
  "profile.regenerate": "Regenerate the character",
  "profile.photo": "Upload a photo instead",
  "profile.features": "Everything else",
  "profile.settings": "Preferences",

  "settings.privacyDefault": "Default privacy",
  "settings.privacyDefaultHint": "The privacy a new entry starts with. You can still change it per entry.",

  "wishlist.title": "Wishlist",
  "wishlist.lede": "Destinations you saved in Discover. Only you can see this list.",
  "wishlist.empty": "Nothing saved yet",
  "wishlist.emptyHint": "Open Discover and save a destination to start planning.",
  "wishlist.remove": "Remove from the wishlist",
  "wishlist.select": "Select",
  "wishlist.selected": "selected",
  "wishlist.makeTrip": "Turn the selection into a trip",
  "wishlist.tripCreated": "Trip created from your wishlist.",
  "wishlist.tripFailed": "The trip could not be created.",
  "wishlist.openDiscover": "Open Discover",

  "budget.title": "Budget",
  "budget.lede": "What your saved destinations would cost, per person.",
  "budget.total": "Wishlist total",
  "budget.perDestination": "Per destination",
  "budget.empty": "No estimate to show",
  "budget.emptyHint": "Save a destination in Discover and its estimate lands here.",
  "budget.noEstimate": "No estimate",

  "stats.title": "Travel stats",
  "stats.lede": "Counted from your own entries and trips.",
  "stats.entries": "Entries",
  "stats.places": "Destinations",
  "stats.trips": "Trips",
  "stats.days": "Days",
  "stats.photos": "Photos",
  "stats.saved": "Wishlist",

  "itinerary.title": "Itinerary",
  "itinerary.lede": "Your active trip, day by day.",
  "itinerary.empty": "No active trip",
  "itinerary.emptyHint": "Set a trip active and its days appear here.",
  "itinerary.day": "Day",
  "itinerary.dayEmpty": "Nothing written for this day yet",
  "itinerary.entryCount": "entries",

  "calendar.title": "Calendar",

  "common.back": "Back",
  "common.save": "Save",
  "common.close": "Close",
  "common.retry": "Retry",
  "common.loading": "Loading",
  "common.km": "km",
  "common.m": "m",

  /* ------------------------------------------------------- round 7 */

  "common.cancel": "Cancel",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.add": "Add",
  "common.unavailable": "Not available",

  "profile.edit": "Edit profile",
  "profile.editHint": "Photo, name, bio, language and default privacy.",
  "profile.identity": "Your account",
  "profile.noBio": "No bio yet.",
  "profile.avatarSection": "Photo",

  "home.bannerLatest": "Your latest entry",
  "home.bannerSaved": "Saved destination",
  "home.bannerStart": "Start your diary",
  "home.bannerStartHint": "Take a photo, write a caption, and this space fills with your own travels.",
  "home.bannerOpen": "Open",
  "home.bannerCapture": "Capture an entry",
  "home.tripEntries": "Entries this trip",
  "home.tripDays": "Days with an entry",

  "itinerary.trip": "Trip",
  "itinerary.plan": "Plan",
  "itinerary.entries": "Diary entries",
  "itinerary.dayPlanEmpty": "Nothing planned for this day",
  "itinerary.addStop": "Add a stop",
  "itinerary.addHere": "Add a stop to this day",
  "itinerary.place": "Place",
  "itinerary.placePlaceholder": "For example Borobudur",
  "itinerary.note": "Note",
  "itinerary.notePlaceholder": "Meeting point, time, ticket",
  "itinerary.fromWishlist": "Or pick one you saved",
  "itinerary.wishlistNone": "Type the place yourself",
  "itinerary.save": "Save stop",
  "itinerary.editStop": "Edit this stop",
  "itinerary.deleteStop": "Remove this stop",
  "itinerary.copyTitle": "Save a plan from another trip",
  "itinerary.copyHint": "A copy is a snapshot. Later edits by the original author never reach yours.",
  "itinerary.copyFrom": "Copy from",
  "itinerary.copy": "Copy the plan",
  "itinerary.copied": "stops copied",
  "itinerary.copyFailed": "The plan could not be copied.",
  "itinerary.unavailable": "Planning is not available on this server yet. The days below still read from your trip.",
  "itinerary.loadFailed": "The plan could not be loaded.",
  "itinerary.saveFailed": "The stop could not be saved.",
  "itinerary.deleteFailed": "The stop could not be removed.",
  "itinerary.noOtherTrip": "You have no other trip to copy from yet.",
  "itinerary.outsideRange": "Outside the trip dates",
  "itinerary.chooseTrip": "Choose a trip",

  "budget.trip": "Trip",
  "budget.addTitle": "Add an expense",
  "budget.editTitle": "Edit this expense",
  "budget.category": "Category",
  "budget.label": "What was it",
  "budget.labelPlaceholder": "For example train to Yogyakarta",
  "budget.amount": "Amount",
  "budget.amountHint": "Whole units. Decimals only where the currency uses them.",
  "budget.amountInvalid": "Enter an amount.",
  "budget.currency": "Currency",
  "budget.date": "Date",
  "budget.note": "Note",
  "budget.addItem": "Add the expense",
  "budget.saveItem": "Save changes",
  "budget.editItem": "Edit this expense",
  "budget.deleteItem": "Delete this expense",
  "budget.items": "Expenses",
  "budget.itemCount": "Expenses",
  "budget.byCategory": "By category",
  "budget.export": "Export CSV",
  "budget.exportUnavailable": "The CSV export is not available on this server yet.",
  "budget.unavailable": "The budget is not available on this server yet.",
  "budget.loadFailed": "The budget could not be loaded.",
  "budget.saveFailed": "The expense could not be saved.",
  "budget.deleteFailed": "The expense could not be deleted.",
  "budget.noTrip": "No active trip",
  "budget.noTripHint": "Create a trip first, then its spending lands here.",
  "budget.nothingYet": "No expenses yet",
  "budget.nothingYetHint": "Add the first one and the totals build themselves.",
  "budget.perCurrency": "Totals are kept per currency and never added together.",

  "budgetCategory.transport": "Transport",
  "budgetCategory.lodging": "Lodging",
  "budgetCategory.food": "Food",
  "budgetCategory.activity": "Activity",
  "budgetCategory.shopping": "Shopping",
  "budgetCategory.other": "Other",

  "stats.serverLede": "Counted by the server from your own history.",
  "stats.localLede": "Counted from the data on this device.",
  "stats.unavailable": "Travel stats are not available on this server yet.",
  "stats.loadFailed": "Travel stats could not be loaded.",
  "stats.distance": "Distance travelled",
  "stats.farthest": "Farthest from home",
  "stats.regions": "Regions visited",
  "stats.provinces": "Provinces visited",
  "stats.regionsEmpty": "No region recorded yet.",
  "stats.nullNote": "A figure the server cannot derive from real history is shown as not available, never as zero.",
  "stats.needsHome": "Needs a home location",
  "stats.needsHomeNote": "Distance from home stays unavailable until the product records a home location. It is an absence, not a zero.",

  "calendar.selected": "Selected day",
  "calendar.jump": "Open this day in the diary",
  "calendar.newTrip": "Start a trip on this day",
  "calendar.newTripTitle": "New trip",
  "calendar.created": "Trip created and set as active.",
  "calendar.createFailed": "The trip could not be created.",
  "calendar.pickDay": "Pick a day to open it or to start a trip on it.",
  "calendar.noEntries": "No entries on this day",
  "calendar.hasEntries": "has entries",

  "wishlist.toItinerary": "Add to the itinerary",
} as const;

export type TranslationKey = keyof typeof en;

const id: Record<TranslationKey, string> = {
  "app.title": "traveldiary",

  "nav.map": "Peta",
  "nav.capture": "Rekam",
  "nav.diary": "Diari",
  "nav.trips": "Perjalanan",
  "nav.explore": "Jelajah",

  "auth.title": "Masuk",
  "auth.signIn": "Masuk",
  "auth.signUp": "Buat akun",
  "auth.lede": "Diari ini publik. Antar pelancong saling terlihat di peta langsung.",
  "auth.username": "Nama pengguna",
  "auth.password": "Kata sandi",
  "auth.displayName": "Nama yang dilihat pelancong lain",
  "auth.toRegister": "Baru di sini, buat akun",
  "auth.toLogin": "Saya sudah punya akun",
  "auth.working": "Mohon tunggu",
  "auth.rejected": "Nama pengguna dan kata sandi tidak cocok.",
  "auth.unreachable": "Server tidak terjangkau. Coba lagi sebentar.",
  "auth.demoHint": "Akun demo: brian dengan kata sandi brian.",
  "auth.signOut": "Keluar",

  "sync.synced": "Tersinkron",
  "sync.syncing": "Menyinkron",
  "sync.pending": "Menunggu",
  "sync.offline": "Luring",

  "map.recenter": "Pusatkan lagi",
  "map.follow": "Ikuti posisi saya",
  "map.unfollow": "Berhenti mengikuti",
  "map.dimension2d": "Tampilan datar",
  "map.dimension3d": "Tampilan miring",
  "map.street": "Peta jalan",
  "map.satellite": "Citra satelit",
  "map.locating": "Mencari posisi kamu",
  "map.locationDenied": "Akses lokasi mati. Nyalakan untuk merekam perjalanan kamu.",
  "map.loadFailed": "Peta gagal dimuat.",
  "map.noTrip": "Belum ada perjalanan aktif",
  "map.noTripHint": "Buka Perjalanan lalu jadikan satu perjalanan aktif untuk mulai merekam.",
  "map.accuracy": "Akurasi",
  "map.you": "Kamu di sini",
  "map.points": "Titik rekaman",
  "map.travellersHere": "Pelancong di sini",
  "map.momentsHere": "Momen di sini",
  "map.momentPin": "Momen diari",
  "map.youSuffix": "(kamu)",
  "map.liveTravellers": "Pelancong daring",
  "map.placeTitle": "Destinasi ini",
  "map.destinations": "Destinasi di sini:",
  "map.diariesHere": "Diari di sini",
  "map.travellerCount": "Pelancong yang menulis di sini:",
  "map.backToPlace": "Kembali ke destinasi ini",
  "map.openTimeline": "Lini masa lengkap",
  "map.showLive": "Tampilkan pelancong yang sedang daring",
  "map.hideLive": "Sembunyikan pelancong yang sedang daring",

  "feed.like": "Sukai catatan ini",
  "feed.unlike": "Batalkan suka",
  "feed.comments": "Komentar",
  "feed.commentPlaceholder": "Tulis komentar",
  "feed.send": "Kirim komentar",
  "feed.commentsUnavailable": "Komentar belum bisa dibuka sekarang.",

  "explore.title": "Jelajah",
  "explore.empty": "Belum ada momen publik",
  "explore.emptyHint": "Saat pelancong lain menulis, momennya muncul di sini.",
  "explore.failed": "Umpan publik gagal dimuat.",
  "explore.someone": "Seorang pelancong",
  "explore.noPlace": "Tempat tanpa nama",
  "explore.entries": "catatan",
  "explore.showOnMap": "Lihat di peta",
  "explore.timelineEmpty": "Belum ada momen publik",
  "explore.timelineEmptyHint": "Pelancong ini belum membagikan apa pun.",
  "explore.timelineFailed": "Lini masa itu gagal dimuat.",

  "capture.title": "Rekam",
  "capture.viewfinder": "Jendela bidik",
  "capture.shutter": "Ambil foto",
  "capture.shutterHint": "Ketuk untuk foto, tekan dan tahan untuk video.",
  "capture.recording": "Merekam",
  "capture.videoLimit": "Video berhenti otomatis di detik ke 60.",
  "capture.gallery": "Unggah dari galeri",
  "capture.cameraUnavailable": "Kamera tidak tersedia di sini. Unggah dari galeri saja.",
  "capture.selected": "Media terpilih",
  "capture.remove": "Hapus",
  "capture.next": "Lanjut",
  "capture.back": "Kembali ke foto",
  "capture.caption": "Keterangan",
  "capture.captionPlaceholder": "Tulis apa yang terjadi di sini.",
  "capture.location": "Lokasi",
  "capture.locationFromGps": "Dari GPS saat pengambilan",
  "capture.locationFromExif": "Dari metadata foto",
  "capture.locationManual": "Diisi olehmu",
  "capture.locationMissing": "Lokasi tidak ditemukan. Isi di bawah supaya catatan ini tidak hilang.",
  "capture.latitude": "Lintang",
  "capture.longitude": "Bujur",
  "capture.placeName": "Nama tempat",
  "capture.placeNamePlaceholder": "Opsional, misalnya Kawah Ijen",
  "capture.time": "Waktu",
  "capture.privacy": "Privasi",
  "capture.save": "Simpan catatan",
  "capture.saving": "Menyimpan",
  "capture.savedOnline": "Catatan tersimpan.",
  "capture.savedQueued": "Catatan masuk antrean, akan tersinkron saat kamu daring lagi.",
  "capture.needMedia": "Tambahkan minimal satu foto atau video dulu.",
  "capture.mediaFull": "Maksimal lima foto untuk satu catatan.",
  "capture.writeLater": "Tulis nanti, unggah dari galeri",
  "capture.locationHow": "Cara menentukan lokasi",
  "capture.locationMap": "Taruh pin",
  "capture.locationSearch": "Alamat",
  "capture.locationPlaces": "Destinasi",
  "capture.locationSearchPlaceholder": "Jalan, tempat, atau kota",
  "capture.locationSearchGo": "Cari alamat ini",
  "capture.locationSearchHint": "Dicari saat kamu kirim, bukan saat mengetik.",
  "capture.locationSearchEmpty": "Tidak ada alamat yang cocok.",
  "capture.locationSearchFailed": "Pencarian alamat sedang tidak tersedia. Taruh pin saja.",
  "capture.locationPlacesPlaceholder": "Saring destinasi",
  "capture.locationPlacesEmpty": "Belum ada destinasi yang cocok.",
  "capture.locationNone": "Lokasi belum diisi. Catatan tetap tersimpan, hanya tidak muncul di peta.",
  "capture.locationClear": "Hapus lokasi",
  "capture.locationFromPhoto": "Dari foto",
  "capture.locationPicked": "Kamu yang memilih",
  "capture.timeFromExif": "Dari metadata foto",
  "capture.timeManual": "Kamu yang mengisi",
  "capture.timeNow": "Sekarang",
  "capture.timeFuture": "Catatan diari tidak bisa di masa depan.",
  "capture.videoUnsupported": "Peramban ini merekam format video yang ditolak server. Catatan tetap di antrean, tambahkan foto atau rekam dari ponsel.",

  "diary.title": "Diari",
  "diary.viewFeed": "Lini masa",
  "diary.viewCalendar": "Kalender",
  "diary.empty": "Belum ada catatan",
  "diary.emptyHint": "Ambil foto dan tulis keterangan untuk memulai diari kamu.",
  "diary.entryCount": "catatan",
  "diary.entryCountOne": "catatan",
  "diary.noCaption": "Tanpa keterangan",
  "diary.queued": "Menunggu sinkron",
  "diary.prevMonth": "Bulan sebelumnya",
  "diary.nextMonth": "Bulan berikutnya",

  "trips.title": "Perjalanan",
  "trips.create": "Perjalanan baru",
  "trips.formTitle": "Buat perjalanan",
  "trips.fieldTitle": "Judul",
  "trips.fieldTitlePlaceholder": "Misalnya Jelajah Jawa",
  "trips.fieldStart": "Tanggal mulai",
  "trips.fieldEnd": "Tanggal selesai",
  "trips.fieldDescription": "Deskripsi",
  "trips.fieldTransport": "Transportasi",
  "trips.save": "Simpan perjalanan",
  "trips.cancel": "Batal",
  "trips.empty": "Belum ada perjalanan",
  "trips.emptyHint": "Buat perjalanan pertama kamu untuk mulai merekam rute.",
  "trips.active": "Aktif",
  "trips.setActive": "Jadikan aktif",
  "trips.history": "Lihat di peta",
  "trips.historyHint": "Semua catatan perjalanan ini, jadi pin dan berurutan menurut waktunya.",
  "trips.statMoments": "Momen",
  "trips.statDays": "Hari",
  "trips.statPoints": "Titik",
  "trips.statDistance": "Jarak",
  "trips.loadFailed": "Perjalanan gagal dimuat. Data lokal yang ditampilkan.",
  "trips.refresh": "Muat ulang",
  "trips.until": "sampai",

  "settings.title": "Pengaturan",
  "settings.language": "Bahasa",
  "settings.indonesian": "Indonesia",
  "settings.english": "Inggris",
  "settings.displayName": "Nama kamu",
  "settings.displayNamePlaceholder": "Terlihat oleh anggota perjalanan",
  "settings.pendingItems": "Data menunggu sinkron",
  "settings.syncNow": "Sinkron sekarang",

  "privacy.private": "Pribadi",
  "privacy.friends": "Teman",
  "privacy.public": "Publik",
  "privacy.hint.private": "Hanya kamu yang bisa melihat.",
  "privacy.hint.friends": "Anggota perjalanan bisa melihat.",
  "privacy.hint.public": "Siapa pun dengan tautan bisa melihat.",

  "transport.walk": "Jalan kaki",
  "transport.bus": "Bus",
  "transport.train": "Kereta",
  "transport.car": "Mobil",
  "transport.motorbike": "Motor",
  "transport.bicycle": "Sepeda",
  "transport.plane": "Pesawat",

  "nav.home": "Beranda",
  "nav.timeline": "Timeline",
  "nav.discover": "Cari",
  "nav.profile": "Profil",

  "home.title": "Beranda",
  "home.features": "Fitur lainnya",
  "home.activity": "Aktivitas terbaru",
  "home.activityAll": "Buka timeline",
  "home.activityEmpty": "Belum ada yang diposting",
  "home.activityEmptyHint": "Saat pelancong menulis catatan, catatannya muncul di sini.",
  "home.activeTrip": "Perjalanan aktif",
  "home.noTrip": "Belum ada perjalanan aktif",
  "home.statPlaces": "Destinasi",
  "home.statSaved": "Wishlist",

  "cari.title": "Cari",
  "discover.lede": "Satu destinasi sekali lihat. Simpan atau lewati.",
  "discover.hint": "Geser kartunya, atau pakai dua tombol di bawah.",
  "discover.accept": "Simpan ke wishlist",
  "discover.acceptShort": "Simpan",
  "discover.dismiss": "Tidak tertarik",
  "discover.dismissShort": "Lewati",
  "discover.saved": "Masuk ke wishlist kamu",
  "discover.skipped": "Dilewati",
  "discover.empty": "Semua destinasi sudah kamu lihat",
  "discover.emptyHint": "Batalkan yang terakhir, atau buka wishlist untuk menyusun perjalanan.",
  "discover.none": "Belum ada destinasi untuk ditampilkan",
  "discover.noneHint": "Destinasi muncul setelah pelancong memposting foto publik.",
  "discover.failed": "Tumpukan destinasi gagal dimuat.",
  "discover.budget": "Perkiraan biaya",
  "discover.budgetDummy": "Estimasi contoh untuk prototipe ini, bukan harga sebenarnya.",
  "discover.perPerson": "per orang",
  "discover.travellers": "pelancong menulis di sini",
  "discover.openWishlist": "Buka wishlist",
  "discover.of": "dari",

  "search.title": "Pencarian",
  "search.open": "Buka pencarian",
  "search.placeholder": "Tempat, pelancong, catatan",
  "search.clear": "Hapus pencarian",
  "search.grid": "Jelajah",
  "search.gridEmpty": "Belum ada foto publik",
  "search.gridEmptyHint": "Catatan publik yang berfoto akan tampil di kisi ini.",
  "search.noResults": "Tidak ada yang cocok",
  "search.noResultsHint": "Coba nama tempat, nama pelancong, atau kata dari keterangan.",
  "search.places": "Tempat",
  "search.travellers": "Pelancong",
  "search.moments": "Catatan",
  "search.typeAll": "Semua",
  "search.typePlace": "Tempat",
  "search.typeTraveller": "Pelancong",
  "search.typeMoment": "Catatan",

  "profil.title": "Profil",
  "profile.account": "Akun kamu",
  "profile.bio": "Bio",
  "profile.bioPlaceholder": "Satu dua baris tentang cara kamu jalan",
  "profile.save": "Simpan profil",
  "profile.saved": "Profil tersimpan.",
  "profile.failed": "Profil gagal disimpan.",
  "profile.avatar": "Karakter kamu",
  "profile.avatarNote": "Mengubah nama tidak pernah mengubah karakter kamu. Benihnya adalah identitas.",
  "profile.regenerate": "Ganti karakter",
  "profile.photo": "Unggah foto saja",
  "profile.features": "Fitur lainnya",
  "profile.settings": "Preferensi",

  "settings.privacyDefault": "Privasi bawaan",
  "settings.privacyDefaultHint": "Privasi awal untuk catatan baru. Tetap bisa diubah per catatan.",

  "wishlist.title": "Wishlist",
  "wishlist.lede": "Destinasi yang kamu simpan di Cari. Hanya kamu yang bisa melihat daftar ini.",
  "wishlist.empty": "Belum ada yang disimpan",
  "wishlist.emptyHint": "Buka Cari lalu simpan satu destinasi untuk mulai menyusun rencana.",
  "wishlist.remove": "Hapus dari wishlist",
  "wishlist.select": "Pilih",
  "wishlist.selected": "dipilih",
  "wishlist.makeTrip": "Jadikan pilihan ini satu perjalanan",
  "wishlist.tripCreated": "Perjalanan dibuat dari wishlist kamu.",
  "wishlist.tripFailed": "Perjalanan gagal dibuat.",
  "wishlist.openDiscover": "Buka Cari",

  "budget.title": "Anggaran",
  "budget.lede": "Perkiraan biaya destinasi simpanan kamu, per orang.",
  "budget.total": "Total wishlist",
  "budget.perDestination": "Per destinasi",
  "budget.empty": "Belum ada perkiraan",
  "budget.emptyHint": "Simpan destinasi di Cari, perkiraannya muncul di sini.",
  "budget.noEstimate": "Tanpa perkiraan",

  "stats.title": "Statistik",
  "stats.lede": "Dihitung dari catatan dan perjalanan kamu sendiri.",
  "stats.entries": "Catatan",
  "stats.places": "Destinasi",
  "stats.trips": "Perjalanan",
  "stats.days": "Hari",
  "stats.photos": "Foto",
  "stats.saved": "Wishlist",

  "itinerary.title": "Rencana",
  "itinerary.lede": "Perjalanan aktif kamu, hari demi hari.",
  "itinerary.empty": "Belum ada perjalanan aktif",
  "itinerary.emptyHint": "Aktifkan satu perjalanan, hari harinya muncul di sini.",
  "itinerary.day": "Hari",
  "itinerary.dayEmpty": "Belum ada catatan untuk hari ini",
  "itinerary.entryCount": "catatan",

  "calendar.title": "Kalender",

  "common.back": "Kembali",
  "common.save": "Simpan",
  "common.close": "Tutup",
  "common.retry": "Coba lagi",
  "common.loading": "Memuat",
  "common.km": "km",
  "common.m": "m",

  /* ------------------------------------------------------- round 7 */

  "common.cancel": "Batal",
  "common.edit": "Ubah",
  "common.delete": "Hapus",
  "common.add": "Tambah",
  "common.unavailable": "Belum tersedia",

  "profile.edit": "Edit profil",
  "profile.editHint": "Foto, nama, bio, bahasa dan privasi bawaan.",
  "profile.identity": "Akun kamu",
  "profile.noBio": "Belum ada bio.",
  "profile.avatarSection": "Foto",

  "home.bannerLatest": "Catatan terbaru kamu",
  "home.bannerSaved": "Destinasi simpanan",
  "home.bannerStart": "Mulai diari kamu",
  "home.bannerStartHint": "Ambil foto, tulis keterangan, dan ruang ini terisi perjalanan kamu sendiri.",
  "home.bannerOpen": "Buka",
  "home.bannerCapture": "Rekam catatan",
  "home.tripEntries": "Catatan perjalanan ini",
  "home.tripDays": "Hari yang ada catatannya",

  "itinerary.trip": "Perjalanan",
  "itinerary.plan": "Rencana",
  "itinerary.entries": "Catatan diari",
  "itinerary.dayPlanEmpty": "Belum ada rencana untuk hari ini",
  "itinerary.addStop": "Tambah tujuan",
  "itinerary.addHere": "Tambah tujuan untuk hari ini",
  "itinerary.place": "Tempat",
  "itinerary.placePlaceholder": "Misalnya Borobudur",
  "itinerary.note": "Catatan",
  "itinerary.notePlaceholder": "Titik kumpul, jam, tiket",
  "itinerary.fromWishlist": "Atau pilih dari simpanan kamu",
  "itinerary.wishlistNone": "Ketik sendiri tempatnya",
  "itinerary.save": "Simpan tujuan",
  "itinerary.editStop": "Ubah tujuan ini",
  "itinerary.deleteStop": "Hapus tujuan ini",
  "itinerary.copyTitle": "Simpan rencana dari perjalanan lain",
  "itinerary.copyHint": "Salinan bersifat potret. Perubahan penulis aslinya tidak ikut berubah di punya kamu.",
  "itinerary.copyFrom": "Salin dari",
  "itinerary.copy": "Salin rencananya",
  "itinerary.copied": "tujuan disalin",
  "itinerary.copyFailed": "Rencana gagal disalin.",
  "itinerary.unavailable": "Fitur rencana belum tersedia di server ini. Hari hari di bawah tetap dibaca dari perjalanan kamu.",
  "itinerary.loadFailed": "Rencana gagal dimuat.",
  "itinerary.saveFailed": "Tujuan gagal disimpan.",
  "itinerary.deleteFailed": "Tujuan gagal dihapus.",
  "itinerary.noOtherTrip": "Belum ada perjalanan lain untuk disalin.",
  "itinerary.outsideRange": "Di luar tanggal perjalanan",
  "itinerary.chooseTrip": "Pilih perjalanan",

  "budget.trip": "Perjalanan",
  "budget.addTitle": "Tambah pengeluaran",
  "budget.editTitle": "Ubah pengeluaran ini",
  "budget.category": "Kategori",
  "budget.label": "Untuk apa",
  "budget.labelPlaceholder": "Misalnya kereta ke Yogyakarta",
  "budget.amount": "Jumlah",
  "budget.amountHint": "Angka bulat. Desimal hanya untuk mata uang yang memakainya.",
  "budget.amountInvalid": "Isi jumlahnya.",
  "budget.currency": "Mata uang",
  "budget.date": "Tanggal",
  "budget.note": "Catatan",
  "budget.addItem": "Tambahkan",
  "budget.saveItem": "Simpan perubahan",
  "budget.editItem": "Ubah pengeluaran ini",
  "budget.deleteItem": "Hapus pengeluaran ini",
  "budget.items": "Pengeluaran",
  "budget.itemCount": "Pengeluaran",
  "budget.byCategory": "Per kategori",
  "budget.export": "Ekspor CSV",
  "budget.exportUnavailable": "Ekspor CSV belum tersedia di server ini.",
  "budget.unavailable": "Anggaran belum tersedia di server ini.",
  "budget.loadFailed": "Anggaran gagal dimuat.",
  "budget.saveFailed": "Pengeluaran gagal disimpan.",
  "budget.deleteFailed": "Pengeluaran gagal dihapus.",
  "budget.noTrip": "Belum ada perjalanan aktif",
  "budget.noTripHint": "Buat perjalanan dulu, pengeluarannya masuk ke sini.",
  "budget.nothingYet": "Belum ada pengeluaran",
  "budget.nothingYetHint": "Tambahkan yang pertama, totalnya terhitung sendiri.",
  "budget.perCurrency": "Total dihitung per mata uang dan tidak pernah dijumlahkan jadi satu.",

  "budgetCategory.transport": "Transportasi",
  "budgetCategory.lodging": "Penginapan",
  "budgetCategory.food": "Makan",
  "budgetCategory.activity": "Aktivitas",
  "budgetCategory.shopping": "Belanja",
  "budgetCategory.other": "Lainnya",

  "stats.serverLede": "Dihitung server dari riwayat kamu sendiri.",
  "stats.localLede": "Dihitung dari data di perangkat ini.",
  "stats.unavailable": "Statistik perjalanan belum tersedia di server ini.",
  "stats.loadFailed": "Statistik perjalanan gagal dimuat.",
  "stats.distance": "Jarak tempuh",
  "stats.farthest": "Terjauh dari rumah",
  "stats.regions": "Wilayah dikunjungi",
  "stats.provinces": "Provinsi dikunjungi",
  "stats.regionsEmpty": "Belum ada wilayah tercatat.",
  "stats.nullNote": "Angka yang tidak bisa diturunkan server dari riwayat asli ditampilkan belum tersedia, bukan nol.",
  "stats.needsHome": "Butuh lokasi rumah",
  "stats.needsHomeNote": "Jarak dari rumah belum bisa dihitung sampai produk ini menyimpan lokasi rumah. Itu ketiadaan data, bukan nol.",

  "calendar.selected": "Hari terpilih",
  "calendar.jump": "Buka hari ini di diari",
  "calendar.newTrip": "Mulai perjalanan di hari ini",
  "calendar.newTripTitle": "Perjalanan baru",
  "calendar.created": "Perjalanan dibuat dan dijadikan aktif.",
  "calendar.createFailed": "Perjalanan gagal dibuat.",
  "calendar.pickDay": "Pilih satu hari untuk membukanya atau memulai perjalanan.",
  "calendar.noEntries": "Tidak ada catatan di hari ini",
  "calendar.hasEntries": "ada catatan",

  "wishlist.toItinerary": "Tambahkan ke rencana",
};

export const dictionaries: Record<Locale, Record<TranslationKey, string>> = {
  id,
  en: en as unknown as Record<TranslationKey, string>,
};

export const DEFAULT_LOCALE: Locale = "id";

const STORAGE_KEY = "traveldiary.locale";

export function readStoredLocale(): Locale {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw === "id" || raw === "en") return raw;
  } catch {
    // Private mode or a WebView with storage disabled, fall through.
  }
  return DEFAULT_LOCALE;
}

export function storeLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // Non fatal, the choice just does not persist.
  }
}

export function translate(locale: Locale, key: TranslationKey): string {
  return dictionaries[locale][key] ?? dictionaries[DEFAULT_LOCALE][key];
}

/** BCP 47 tag for Intl formatting, derived from the app locale. */
export function intlLocale(locale: Locale): string {
  return locale === "id" ? "id-ID" : "en-GB";
}
